import {
  AvailabilitiesService,
  AvailabilityMessage,
  TutorsService,
} from "client/openapi";
import type { TutorBooking } from "client/openapi/models/TutorBooking";
import moment from "moment";
import { Fragment, ReactNode, useEffect, useState } from "react";
import { formatTimeRange } from "util/time";
import TimeGrid, {
  Block,
  Column,
  fits,
  GridPanel,
  LANE_COLORS,
  minutesInto,
  overlaps,
  Span,
} from "./TimeGrid";

/** Days shown at a time; the arrows page through the next ones. */
const DAYS = 5;

/** Start and end, in milliseconds. */
type Interval = [number, number];

/** Sorted, with touching or overlapping intervals joined into one. */
const merge = (intervals: Interval[]): Interval[] =>
  [...intervals]
    .sort((a, b) => a[0] - b[0])
    .reduce<Interval[]>((merged, [from, to]) => {
      const last = merged[merged.length - 1];
      if (last && from <= last[1]) last[1] = Math.max(last[1], to);
      else merged.push([from, to]);
      return merged;
    }, []);

/** A tutor's free time. The server sends it as 15-minute slots; they join up. */
const toIntervals = (availabilities: AvailabilityMessage[]): Interval[] =>
  merge(
    availabilities.map((availability): Interval => {
      const from = moment(availability.start);
      return [
        from.valueOf(),
        from.clone().add(availability.duration, "minutes").valueOf(),
      ];
    })
  );

/** The parts of the intervals on `day`, in minutes from its start. */
const spansOn = (day: moment.Moment, intervals: Interval[]): Span[] => {
  const dayStart = day.valueOf();
  const dayEnd = day.clone().add(1, "day").valueOf();
  return intervals
    .filter(([from, to]) => from < dayEnd && to > dayStart)
    .map(([from, to]) => ({
      from: minutesInto(day, moment(Math.max(from, dayStart))),
      to: minutesInto(day, moment(Math.min(to, dayEnd))),
    }));
};

/** A booking on `day` as a block of its column; a request is dashed. */
function blocksOn(day: moment.Moment, bookings: TutorBooking[]): Block[] {
  const dayStart = day.valueOf();
  const dayEnd = day.clone().add(1, "day").valueOf();
  return bookings
    .filter(
      (booking) =>
        moment(booking.start).valueOf() < dayEnd &&
        moment(booking.end).valueOf() > dayStart
    )
    .map((booking, i) => {
      const from = moment(booking.start);
      const to = moment(booking.end);
      return {
        id: i,
        label: `${
          booking.label ?? (booking.pending ? "Request" : "Booked")
        }, ${formatTimeRange(from.toDate(), to.toDate())}`,
        from: minutesInto(day, from),
        to: minutesInto(day, to),
        pending: !!booking.pending,
      };
    })
    .sort((a, b) => a.from - b.from);
}

/**
 * Five days around the meeting's date (it in the middle) for the selected
 * tutors (in the calendar pop-up, its week): their free
 * time shaded and their meetings as busy, named only where the viewer's own
 * calendar shows them. With several tutors, each has a lane in their own
 * color, in the order chosen. Any time can be picked, setting the date and
 * start time; outside someone's free time or over a booking, This meeting
 * turns red.
 */
export default function TutorAvailabilityGrid({
  orgId,
  tutors,
  start,
  duration,
  editing,
  focus,
  week = false,
  columnsInView,
  empty,
  onPick,
  close,
}: {
  orgId: number;
  /** The selected tutors, in the order chosen. */
  tutors: { id: number; name: string }[];
  /** The meeting being booked: its start and length. */
  start: string;
  duration: number;
  /**
   * The meeting being edited, as saved. It is left out of the bookings, and
   * since /availabilities/tutor takes it out of its tutors' free time, its
   * time is added back for them, as AvailabilityDayPicker does.
   */
  editing?: { id: number; start: string; duration: number; tutorIds: number[] };
  /**
   * Another day to show, in green (a week of the series picked in the form):
   * the days move to it when it changes, and back to the meeting's date when
   * it is cleared.
   */
  focus?: string;
  /** The calendar pop-up: the meeting's week, the arrows stepping a week. */
  week?: boolean;
  /** The calendar pop-up: this many days in view, the rest scroll sideways. */
  columnsInView?: number;
  /** Shown under the no-tutor line: the pop-up puts its Tutors field here. */
  empty?: ReactNode;
  onPick: (start: string) => void;
  /** Only in the calendar pop-up, where the grid takes the form's place. */
  close?: { label: string; onClick: () => void };
}) {
  const [middle, setMiddle] = useState(() => moment(start).startOf("day"));
  // Per tutor, in the order of `tutors`.
  const [free, setFree] = useState<Interval[][]>();
  const [bookings, setBookings] = useState<TutorBooking[][]>();
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  const span = week ? 7 : DAYS;
  const first = week
    ? middle.clone().startOf("week")
    : middle.clone().subtract(Math.floor(DAYS / 2), "days");
  const firstKey = first.valueOf();
  const days = Array.from({ length: span }, (_, i) =>
    first.clone().add(i, "days")
  );
  const windowStart = days[0];
  const lastDay = days[days.length - 1];
  const windowEnd = lastDay.clone().add(1, "day");
  const tutorKey = tutors.map((tutor) => tutor.id).join(",");
  const editingId = editing?.id;
  const editingStart = editing?.start;
  const editingDuration = editing?.duration;
  const editingTutorKey = editing?.tutorIds.join(",") ?? "";

  // A date set outside the shown days brings them to it.
  useEffect(() => {
    const date = moment(start);
    if (date.isBefore(windowStart) || !date.isBefore(windowEnd)) {
      setMiddle(date.clone().startOf("day"));
    }
    // Only a new start moves the days; the arrows move them otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  useEffect(() => {
    const day = moment(focus ?? start).startOf("day");
    setMiddle((shown) => (shown.isSame(day) ? shown : day));
    // Only a new focus moves the days here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  useEffect(() => {
    if (!tutorKey) return;
    let cancelled = false;
    setFree(undefined);
    setBookings(undefined);
    setFailed(false);
    const ids = tutorKey.split(",").map(Number);
    const from = moment(firstKey);
    const until = from.clone().add(span, "days");
    Promise.all([
      Promise.all(
        ids.map((tutorId) =>
          AvailabilitiesService.getAvailabilitiesByTutor({
            tutorId,
            start: from.toISOString(),
            until: until.toISOString(),
            // Every free stretch, however short.
            duration: 15,
          })
        )
      ),
      // One call per tutor, for their own lane.
      Promise.all(
        ids.map((tutorId) =>
          TutorsService.getTutorBookings({
            orgId,
            tutorIds: [tutorId],
            start: from.toISOString(),
            until: until.toISOString(),
            excludeMeetingId: editingId,
          })
        )
      ),
    ])
      .then(([perTutor, booked]) => {
        if (cancelled) return;
        // Only the edited meeting's own tutors had its time taken out.
        const savedTutors = new Set(editingTutorKey.split(",").map(Number));
        const savedTime: Interval | undefined = editingStart
          ? [
              moment(editingStart).valueOf(),
              moment(editingStart).add(editingDuration, "minutes").valueOf(),
            ]
          : undefined;
        setFree(
          perTutor.map((availabilities, i) => {
            const free = toIntervals(availabilities);
            return savedTime && savedTutors.has(ids[i])
              ? merge([...free, savedTime])
              : free;
          })
        );
        setBookings(booked);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    firstKey,
    span,
    orgId,
    tutorKey,
    editingId,
    editingStart,
    editingDuration,
    editingTutorKey,
    reload,
  ]);

  const thisStart = moment(start);
  const loading = !free || !bookings;

  const columns: Column[] = days.map((day) => {
    const lanes = tutors.map((tutor, i) => ({
      key: String(tutor.id),
      blocks: blocksOn(day, bookings?.[i] ?? []),
      free: spansOn(day, free?.[i] ?? []),
    }));
    const isMeetingDay = thisStart.isSame(day, "day");
    const isFocus = !!focus && moment(focus).isSame(day, "day");
    const thisFrom = minutesInto(day, thisStart);
    const thisMeeting = {
      from: thisFrom,
      to: Math.min(24 * 60, thisFrom + duration),
    };
    const busy = lanes.some((lane) => overlaps(thisMeeting, lane.blocks));
    const freeFor = lanes.filter((lane) => fits(thisMeeting, lane.free)).length;
    return {
      key: day.format("YYYY-MM-DD"),
      title: day.format("ddd"),
      subtitle: day.format("MMM D"),
      hint: day.format("dddd, MMMM D"),
      highlighted: isMeetingDay || isFocus,
      lanes,
      thisMeeting: isMeetingDay
        ? { ...thisMeeting, clash: busy || freeFor < lanes.length }
        : undefined,
      pickTitle: `Pick ${day.format("ddd, MMM D")}`,
    };
  });

  return (
    <GridPanel
      label={`${windowStart.format("MMM D")} – ${lastDay.format("MMM D")}`}
      previous={{
        label: week ? "Previous week" : "Previous days",
        onClick: () => setMiddle(middle.clone().subtract(span, "days")),
      }}
      next={{
        label: week ? "Next week" : "Next days",
        onClick: () => setMiddle(middle.clone().add(span, "days")),
      }}
      showsFree
      // Several tutors: whose lane is whose.
      legend={
        tutors.length > 1 ? (
          <>
            {tutors.map((tutor, i) => (
              <Fragment key={tutor.id}>
                <span
                  className={`inline-block h-3 w-3 rounded-sm ${
                    LANE_COLORS[i % LANE_COLORS.length].swatch
                  }`}
                />
                <span
                  className="mr-2 max-w-[10rem] truncate"
                  title={tutor.name}
                >
                  {tutor.name}
                </span>
              </Fragment>
            ))}
            <span className="text-gray-400">
              Light: free · Dark: booked · Dashed: request
            </span>
          </>
        ) : undefined
      }
      failed={failed}
      failedText="Could not load tutor availability."
      onRetry={() => setReload(reload + 1)}
      close={close}
    >
      {tutors.length === 0 ? (
        empty ? (
          <div className="py-3">
            <p className="mb-2 text-sm text-gray-500">
              Select a tutor to see their availability
            </p>
            {empty}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-gray-500">
            Select a tutor to see their availability
          </p>
        )
      ) : (
        <TimeGrid
          columns={columns}
          loading={loading}
          columnsInView={columnsInView}
          hoverMinutes={duration}
          // Opens about an hour above the meeting's time, or 8 AM when its
          // date is not shown.
          focusMinutes={
            days.some((day) => thisStart.isSame(day, "day"))
              ? minutesInto(thisStart.clone().startOf("day"), thisStart)
              : 8 * 60
          }
          onPick={(column, minutes) =>
            // Setting minutes past 59 rolls into the hours on the clock.
            onPick(moment(column.key).startOf("day").minutes(minutes).format())
          }
        />
      )}
    </GridPanel>
  );
}
